'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@icons/classic/regular';
import { createColumnHelper } from '@tanstack/react-table';

import type { CellContext, ColumnDef } from '@tanstack/react-table';

const EXPAND_COLUMN_WIDTH = 36;

/**
 * The toggle is a component at module scope because `flexRender` renders a cell
 * renderer as a component type: a closure built per `createExpandColumn()` call
 * would be a new type on every render, remounting the button and dropping the
 * focus of the keyboard user who just pressed it.
 */
function ExpandRowToggle<TData>({
  row,
  isExpanded: injectedExpanded,
}: CellContext<TData, unknown> & { isExpanded?: boolean }) {
  if (!row.getCanExpand()) return null;

  // General hazard: a named, top-level, compiler-memoized cell component that reads live table
  // state (`getIsExpanded`, `getIsSelected`, …) goes stale, because `cell.getContext()` is
  // identity-stable across the state change so the compiler never re-runs it. The body works
  // around it here by injecting the live flag; author state-reading cells with that in mind.
  // Fall back to the live read for standalone use (e.g. tests) where nothing injects it.
  const isExpanded = injectedExpanded ?? row.getIsExpanded();

  return (
    <button
      className="inline-flex size-5 items-center justify-center text-content-secondary"
      aria-expanded={isExpanded}
      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
      onClick={(e) => {
        e.stopPropagation();
        row.toggleExpanded();
      }}
      type="button"
    >
      <FontAwesomeIcon className="size-3" icon={isExpanded ? faChevronDown : faChevronRight} />
    </button>
  );
}

/** Empty header, hoisted for the same reason as the cell. */
const EmptyHeader = () => null;

/**
 * Factory for the leading chevron column used by the shared row-expansion
 * feature. Each cell toggles its row's expanded state; rows that can't expand
 * render an empty cell, and the header is empty.
 *
 * `meta.isExpandColumn` tells the table body renderers to skip the row-level
 * `<Link>` wrap for this cell so toggling never triggers row navigation. Clicks
 * also stop propagation so `onRowClick` rows aren't opened.
 */
export function createExpandColumn<TData>(): ColumnDef<TData, unknown> {
  const columnHelper = createColumnHelper<TData>();

  return columnHelper.display({
    id: 'expand',
    size: EXPAND_COLUMN_WIDTH,
    enableSorting: false,
    meta: { isExpandColumn: true, style: { width: EXPAND_COLUMN_WIDTH } },
    header: EmptyHeader,
    cell: ExpandRowToggle,
  });
}
